import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, Phone, MapPin, CheckCircle2 } from 'lucide-react';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    investmentRange: '',
    message: '',
    consent: false,
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const investmentRanges = [
    'Under AED 500K',
    'AED 500K - 1M',
    'AED 1M - 2M',
    'AED 2M - 5M',
    'Above AED 5M',
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.country.trim()) newErrors.country = 'Country is required';
    if (!formData.consent) newErrors.consent = 'You must provide consent to proceed';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      try {
        // Send email using Web3Forms
        const response = await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            access_key: '2c149aad-6814-4041-9d5c-73e74c5009d0', // Free Web3Forms key for sales@navaproperties.me
            subject: 'New Investment Inquiry from Nava Properties Website',
            name: formData.name,
            email: formData.email,
            replyto: formData.email,
            message: `
New Contact Form Submission:

Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Country: ${formData.country}
Investment Range: ${formData.investmentRange || 'Not specified'}

Message:
${formData.message || 'No message provided'}

---
This inquiry was submitted through the Nava Properties website contact form.
            `.trim()
          }),
        });

        const data = await response.json();
        
        if (data.success) {
          console.log('Form submitted successfully:', formData);
          setSubmitted(true);
          
          // Reset form after 3 seconds
          setTimeout(() => {
            setFormData({
              name: '',
              email: '',
              phone: '',
              country: '',
              investmentRange: '',
              message: '',
              consent: false,
            });
            setSubmitted(false);
          }, 3000);
        } else {
          alert('There was an error submitting your form. Please try again or contact us directly.');
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        alert('There was an error submitting your form. Please try again or contact us directly.');
      }
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      value: 'sales@navaproperties.me',
      link: 'mailto:sales@navaproperties.me'
    },
    {
      icon: Phone,
      title: 'Call Us',
      value: '+971 5267 80561',
      link: 'tel:+971526780561'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      value: 'OFFICE 1 XAVIER BUSINESS CENTRE, IBN BATUTA GATE OFFICE. DUBAI',
      link: '#'
    },
  ];

  return (
    <section id="contact" className="relative py-24 overflow-hidden bg-white">
      {/* Background effects */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-yellow-500/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-5xl md:text-7xl font-bold mb-6">
            <span className="text-gray-800">Begin Your</span>
            <br />
            <span className="bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 bg-clip-text text-transparent">
              Wealth Journey
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Schedule a consultation with our experts. No obligations, just insights.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
          {/* Contact form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gradient-to-br from-[#FFF8DC] to-[#F5F5DC] border border-yellow-200 rounded-3xl p-8 md:p-12 shadow-lg">
              {!submitted ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full px-4 py-4 bg-black/50 border ${
                        errors.name ? 'border-red-500' : 'border-yellow-400/30'
                      } rounded-xl text-gray-300 focus:outline-none focus:border-yellow-400 transition-colors`}
                      placeholder="John Doe"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-4 bg-black/50 border ${
                        errors.email ? 'border-red-500' : 'border-yellow-400/30'
                      } rounded-xl text-gray-300 focus:outline-none focus:border-yellow-400 transition-colors`}
                      placeholder="john@example.com"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                    )}
                  </div>

                  {/* Phone and Country */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={`w-full px-4 py-4 bg-black/50 border ${
                          errors.phone ? 'border-red-500' : 'border-yellow-400/30'
                        } rounded-xl text-gray-300 focus:outline-none focus:border-yellow-400 transition-colors`}
                        placeholder="+1 234 567 890"
                      />
                      {errors.phone && (
                        <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        Country *
                      </label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className={`w-full px-4 py-4 bg-black/50 border ${
                          errors.country ? 'border-red-500' : 'border-yellow-400/30'
                        } rounded-xl text-gray-300 focus:outline-none focus:border-yellow-400 transition-colors`}
                        placeholder="United States"
                      />
                      {errors.country && (
                        <p className="text-red-500 text-sm mt-1">{errors.country}</p>
                      )}
                    </div>
                  </div>

                  {/* Investment Range */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      Investment Range
                    </label>
                    <select
                      name="investmentRange"
                      value={formData.investmentRange}
                      onChange={handleChange}
                      className={`w-full px-4 py-4 bg-black/50 border ${
                        errors.investmentRange ? 'border-red-500' : 'border-yellow-400/30'
                      } rounded-xl text-gray-300 focus:outline-none focus:border-yellow-400 transition-colors`}
                    >
                      <option value="">Select investment range</option>
                      {investmentRanges.map((range) => (
                        <option key={range} value={range}>
                          {range}
                        </option>
                      ))}
                    </select>
                    {errors.investmentRange && (
                      <p className="text-red-500 text-sm mt-1">{errors.investmentRange}</p>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      Message (Optional)
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-4 bg-black/50 border border-yellow-400/30 rounded-xl text-gray-300 focus:outline-none focus:border-yellow-400 transition-colors resize-none"
                      placeholder="Tell us about your investment goals..."
                    />
                  </div>

                  {/* Consent */}
                  <div className="bg-yellow-400/5 border border-yellow-400/30 rounded-xl p-6">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="consent"
                        checked={formData.consent}
                        onChange={handleChange}
                        className="mt-1 w-5 h-5 accent-yellow-400"
                      />
                      <div>
                        <span className="text-gray-900 leading-relaxed font-medium">
                          I consent to be contacted by Nava Properties regarding investment opportunities.
                        </span>
                        <p className="text-yellow-600 font-semibold text-sm mt-2">
                          We will not call/email/message you without your consent, ever.
                        </p>
                      </div>
                    </label>
                    {errors.consent && (
                      <p className="text-red-500 text-sm mt-2">{errors.consent}</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    className="w-full py-5 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold text-lg rounded-xl shadow-2xl shadow-yellow-500/50 hover:shadow-yellow-500/80 transition-all duration-300 flex items-center justify-center gap-3"
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span>Start Your Journey</span>
                    <Send className="w-5 h-5" />
                  </motion.button>
                </form>
              ) : (
                <motion.div
                  className="text-center py-20"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <motion.div
                    className="w-24 h-24 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-6"
                    animate={{
                      scale: [1, 1.2, 1],
                      rotate: [0, 360],
                    }}
                    transition={{ duration: 1 }}
                  >
                    <CheckCircle2 className="w-12 h-12 text-black" />
                  </motion.div>
                  <h3 className="text-3xl font-bold text-yellow-400 mb-4">
                    Thank You!
                  </h3>
                  <p className="text-gray-300 text-lg">
                    We've received your inquiry and will contact you within 24 hours.
                  </p>
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Contact info */}
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <motion.a
                  key={index}
                  href={info.link}
                  className="block group"
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  whileHover={{ x: 10 }}
                >
                  <div className="bg-gradient-to-br from-white to-gray-50 border border-yellow-400/30 rounded-3xl p-8 group-hover:border-yellow-400/60 hover:shadow-lg transition-all duration-500">
                    <div className="flex items-center gap-6">
                      <motion.div
                        className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-2xl flex items-center justify-center flex-shrink-0"
                        whileHover={{ rotate: 360, scale: 1.1 }}
                        transition={{ duration: 0.6 }}
                      >
                        <Icon className="w-8 h-8 text-white" />
                      </motion.div>
                      
                      <div>
                        <h4 className="text-gray-600 font-medium mb-1">{info.title}</h4>
                        <p className="text-xl font-bold text-gray-800 group-hover:text-yellow-600 transition-colors">
                          {info.value}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.a>
              );
            })}

            {/* Additional info card */}
            <motion.div
              className="bg-gradient-to-br from-yellow-50 to-white border border-yellow-400/30 rounded-3xl p-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
            >
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                Why Choose Nava Properties?
              </h3>
              <ul className="space-y-3">
                {[
                  'No false promises - only data-driven insights',
                  '7+ years of Dubai market expertise',
                  'Transparent, ethical investment approach',
                  '100+ successful global investors',
                  'Exclusive access to premium properties'
                ].map((item, idx) => (
                  <motion.li
                    key={idx}
                    className="flex items-start gap-3 text-gray-700"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 + idx * 0.1 }}
                  >
                    <CheckCircle2 className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
